// COPY de apps/desktop/src/shared/http/errors.ts; dedup em F13.
import axios, { type AxiosError } from 'axios';

export class NotImplementedError extends Error {
  readonly code = 'not_implemented';
  readonly availableIn: string;

  constructor(message: string, availableIn: string) {
    super(message);
    this.name = 'NotImplementedError';
    this.availableIn = availableIn;
  }
}

export function isNotImplemented(err: unknown): err is NotImplementedError {
  return err instanceof NotImplementedError;
}

interface FastApiValidationItem {
  loc: (string | number)[];
  msg: string;
  type?: string;
}

export interface ApiErrorPayload {
  detail?: string | FastApiValidationItem[];
  code?: string;
  message?: string;
  // Shape real do `register_error_handlers` (common/errors.py) para
  // `DomainError`: `{"error": {"code", "message"}}`. `code`/`message` no
  // topo ficam como fallback para payloads que não passam por lá.
  error?: {
    code?: string;
    message?: string;
  };
}

export interface ApiError {
  status: number;
  code: string;
  message: string;
  requestId: string | null;
}

function friendlyMessageForStatus(status: number): string {
  if (status === 0)
    return 'Sem conexão com o servidor. Verifique sua rede ou se o backend está no ar.';
  if (status === 400) return 'Dados inválidos na requisição.';
  if (status === 401) return 'Sessão expirada. Faça login novamente.';
  if (status === 403) return 'Você não tem permissão para esta ação.';
  if (status === 404) return 'Recurso não encontrado.';
  if (status === 409) return 'Conflito com o estado atual do recurso.';
  if (status === 422) return 'Validação dos dados falhou.';
  if (status === 429) return 'Muitas requisições em pouco tempo. Tente novamente em instantes.';
  if (status === 501) return 'Funcionalidade ainda não disponível.';
  if (status >= 500) return 'Erro interno do servidor. A equipe foi notificada.';
  return 'Erro inesperado.';
}

function extractDetail(detail: ApiErrorPayload['detail']): string | null {
  if (detail === undefined || detail === null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        const field = item.loc.filter((part) => part !== 'body').join('.');
        return field.length > 0 ? `${field}: ${item.msg}` : item.msg;
      })
      .join(' · ');
  }
  return null;
}

export function toApiError(err: unknown): ApiError {
  if (axios.isAxiosError(err)) {
    const axErr = err as AxiosError<ApiErrorPayload>;
    const status = axErr.response?.status ?? 0;
    const data = axErr.response?.data;
    const detailMessage = extractDetail(data?.detail);
    const message =
      detailMessage ?? data?.error?.message ?? data?.message ?? friendlyMessageForStatus(status);
    const requestId = (axErr.response?.headers?.['x-request-id'] as string | undefined) ?? null;
    const code =
      data?.error?.code ?? data?.code ?? (status === 0 ? 'network_error' : `http_${status}`);
    return { status, code, message, requestId };
  }
  const message = err instanceof Error ? err.message : 'Erro inesperado.';
  return { status: 0, code: 'unknown', message, requestId: null };
}
